import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PieChart, Crown, Users, Plane } from "lucide-react";

const MY_COLOR = "#0ea5e9";
const RIVAL_COLORS = ["#f43f5e", "#f59e0b", "#8b5cf6", "#ec4899", "#84cc16", "#06b6d4"];

function ShareBar({ myShare, myName, rivals }) {
  const segments = [
    { name: myName, pct: myShare, color: MY_COLOR, mine: true },
    ...rivals.map((r, i) => ({ name: r.team_name, pct: r.share_pct, color: RIVAL_COLORS[i % RIVAL_COLORS.length] })),
  ].filter(s => s.pct > 0);
  return (
    <div className="flex h-6 w-full overflow-hidden rounded-md bg-slate-100" data-testid="market-share-bar">
      {segments.map((s, i) => (
        <div
          key={i}
          className="h-full flex items-center justify-center text-[10px] font-semibold text-white transition-all"
          style={{ width: `${s.pct}%`, backgroundColor: s.color, minWidth: s.pct > 0 ? "2px" : 0 }}
          title={`${s.name}: ${s.pct}%`}
        >
          {s.pct >= 12 ? `${s.pct}%` : ""}
        </div>
      ))}
    </div>
  );
}

export default function MarketShareSection({ game, team }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedQuarter, setSelectedQuarter] = useState(null);

  useEffect(() => {
    if (!team?.id) return;
    setLoading(true);
    axios.get(`${API}/teams/${team.id}/market-share`)
      .then(res => {
        setData(res.data);
        const qs = res.data?.quarters || [];
        if (qs.length) setSelectedQuarter(qs[qs.length - 1].quarter);
      })
      .catch(() => setData({ quarters: [] }))
      .finally(() => setLoading(false));
  }, [team?.id, game?.current_quarter]);

  const quarters = data?.quarters || [];
  const current = useMemo(
    () => quarters.find(q => q.quarter === selectedQuarter) || null,
    [quarters, selectedQuarter]
  );

  if (loading) {
    return <div className="text-center py-16 text-slate-400" data-testid="market-share-loading">{t("common.loading")}</div>;
  }

  if (!quarters.length) {
    return (
      <Card data-testid="market-share-empty">
        <CardContent className="py-16 text-center text-slate-500">
          <PieChart className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          {t("marketShare.empty")}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="market-share-section">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><PieChart className="w-5 h-5 text-sky-500" /> {t("marketShare.title")}</h2>
          <p className="text-sm text-slate-500">{t("marketShare.subtitle")}</p>
        </div>
        <Select value={String(selectedQuarter)} onValueChange={(v) => setSelectedQuarter(Number(v))}>
          <SelectTrigger className="w-40" data-testid="market-share-quarter-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {quarters.map(q => (
              <SelectItem key={q.quarter} value={String(q.quarter)} data-testid={`quarter-option-${q.quarter}`}>
                {t("marketShare.quarter", { n: q.quarter })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {current && current.markets.map((m, idx) => (
        <Card key={m.city} data-testid={`market-card-${idx}`}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Plane className="w-4 h-4 text-slate-400" /> {m.city}
                {!m.contested && (
                  <span className="text-[10px] font-medium uppercase tracking-wide text-emerald-600 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                    {t("marketShare.monopoly")}
                  </span>
                )}
              </CardTitle>
              <div className="text-right">
                <div className="text-xl font-bold text-sky-600" data-testid={`my-share-${idx}`}>{m.my_share_pct}%</div>
                <div className="text-[11px] text-slate-400">{t("marketShare.yourShare")}</div>
              </div>
            </div>
            <CardDescription className="text-xs">
              {t("marketShare.captured", { pax: m.my_passengers.toLocaleString(), total: m.total_passengers.toLocaleString(), demand: m.demand.toLocaleString() })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <ShareBar myShare={m.my_share_pct} myName={team?.name} rivals={m.rivals} />
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-sm" data-testid={`me-row-${idx}`}>
                <span className="flex items-center gap-2 font-medium">
                  <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: MY_COLOR }} />
                  {team?.name}
                  {m.my_share_pct >= 50 && m.contested && <Crown className="w-3.5 h-3.5 text-amber-500" />}
                </span>
                <span className="font-mono">{m.my_passengers.toLocaleString()} · {m.my_share_pct}%</span>
              </div>
              {m.rivals.map((r, i) => (
                <div key={i} className="flex items-center justify-between text-sm text-slate-600" data-testid={`rival-row-${idx}-${i}`}>
                  <span className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: RIVAL_COLORS[i % RIVAL_COLORS.length] }} />
                    {r.team_name}
                  </span>
                  <span className="font-mono">{r.passengers.toLocaleString()} · {r.share_pct}%</span>
                </div>
              ))}
              {!m.rivals.length && (
                <div className="flex items-center gap-2 text-xs text-slate-400"><Users className="w-3.5 h-3.5" /> {t("marketShare.noRivals")}</div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
